/**
 * The ONLY file that imports `<Puck>`.
 *
 * Puck is pre-1.0 and pinned to an exact version. Keeping the import here means a breaking
 * release costs this one file rather than the whole block library — which is also why
 * `packages/blocks` declares fields with our own `FieldSpec` union instead of Puck's.
 */

import { useCallback, useMemo, type ComponentProps } from 'react';
import { Puck, type Data } from '@puckeditor/core';
// Puck exposes its stylesheet as `./puck.css`, not the `./styles.css` most examples show.
import '@puckeditor/core/puck.css';

import { useDocStore } from '../store/docStore.js';
import { buildPuckConfig } from './buildPuckConfig.js';
import { ExportPanel } from '../panels/ExportPanel.js';
import { BrandLockup } from '../brand.js';
import { PageSettingsPanel, SaveStatus } from '../panels/PageSettingsPanel.js';
import { PagesPanel } from '../panels/PagesPanel.js';

/**
 * Preview breakpoints, matching Webflow's exactly.
 *
 * Not approximations: 1280 / 991 / 767 / 479 are the widths Webflow's own cascade keys off,
 * so what an author sees here is what the pasted section will do in the Designer.
 */
const VIEWPORTS = [
  { width: 1440, label: 'Desktop', icon: 'Monitor' as const },
  { width: 1280, label: 'Large' },
  { width: 991, label: 'Tablet', icon: 'Tablet' as const },
  { width: 767, label: 'Mobile' },
  { width: 479, label: 'Small', icon: 'Smartphone' as const },
];

/**
 * Defined at module scope, not inline.
 *
 * Puck renders `renderHeaderActions` as a component. An inline arrow is a new function
 * identity on every render, which React reads as a *different component type* — so it
 * unmounted and remounted this whole subtree each time anything in the store changed.
 * The visible symptom was typing in Page settings: the first keystroke updated the store,
 * the store re-rendered the editor, the input was destroyed and recreated, and focus went
 * with it. One character per click.
 */
function HeaderActions(): React.ReactElement {
  return (
    <div className="lp-header-actions">
      <BrandLockup />
      <SaveStatus />
      <PagesPanel />
      <PageSettingsPanel />
      <ExportPanel />
    </div>
  );
}

const renderHeaderActions = (): React.ReactElement => <HeaderActions />;

/**
 * Keep rich text as the HTML string Puck actually stores.
 *
 * Puck's default `richtext` transform swaps the prop for a React element — an inline Tiptap
 * editor — before calling a component's `render()`. Our blocks do not render JSX; they return
 * IR that three consumers walk, and the exporters read the *stored* data rather than the
 * transformed prop. So the default transform made the canvas print `[object Object]` while the
 * Webflow export emitted correct markup: preview and export disagreeing, which is the one
 * failure mode this architecture is built to make impossible.
 *
 * Returning the value untouched restores that guarantee. The inline on-canvas editor is no
 * loss — the canvas is rendered by `renderIR`, so Puck was never able to place its editor in
 * our tree anyway. Rich text is edited in the fields panel.
 */
const fieldTransforms: ComponentProps<typeof Puck>['fieldTransforms'] = {
  richtext: ({ value }) => value,
};

export function EditorHost(): React.ReactElement {
  // Narrow selectors, so editing the page title does not re-render the canvas.
  const docType = useDocStore((s) => s.docType);
  const geo = useDocStore((s) => s.meta.geo);
  const slug = useDocStore((s) => s.meta.slug);
  const title = useDocStore((s) => s.title);
  const puck = useDocStore((s) => s.puck);
  const setPuck = useDocStore((s) => s.setPuck);

  const config = useMemo(() => buildPuckConfig({ docType, geo }), [docType, geo]);
  const onChange = useCallback((data: Data) => setPuck(data as never), [setPuck]);

  return (
    <Puck
      config={config}
      data={puck as Data}
      onChange={onChange}
      viewports={VIEWPORTS}
      // An iframe means real CSS media queries apply, rather than a width simulation that
      // lies about anything container- or viewport-relative.
      iframe={{ enabled: true }}
      headerTitle={title}
      headerPath={slug ? `/${slug}` : undefined}
      renderHeaderActions={renderHeaderActions}
      fieldTransforms={fieldTransforms}
    />
  );
}
